<template>
  <div>
    <div class="text-center font-medium mb-6">
      Editá tus datos
    </div>

    <form @submit.prevent="onSave" class="grid grid-cols-1 gap-3 lg:grid-cols-2 lg:gap-4">
      <div>
        <label class="form-label">Nombre</label>
        <input
          v-model="form.firstName"
          type="text"
          required
          class="form-input lg:text-base"
        />
      </div>
      <div>
        <label class="form-label">Apellido</label>
        <input
          v-model="form.lastName"
          type="text"
          required
          class="form-input lg:text-base"
        />
      </div>

      <div>
        <label class="form-label">Tipo de Documento</label>
        <Listbox v-model="form.docType">
          <div class="relative">
            <ListboxButton
              class="form-listbox-btn lg:text-base"
            >
              <span class="block truncate">{{ form.docType }}</span>
              <span
                class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none"
              >
                <SelectorIcon class="w-5 h-5 text-gray-400" aria-hidden="true" />
              </span>
            </ListboxButton>
            <transition
              leave-active-class="transition duration-100 ease-in"
              leave-from-class="opacity-100"
              leave-to-class="opacity-0"
            >
              <ListboxOptions
                class="absolute w-full py-1 mt-1 overflow-auto bg-white z-10 rounded-lg shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none"
              >
                <ListboxOption
                  v-slot="{ active, selected }"
                  v-for="opt in docList"
                  :key="opt"
                  :value="opt"
                  as="template"
                >
                  <li
                    :class="[
                      active ? 'text-white bg-aces' : 'text-black',
                      'cursor-default select-none relative py-2 pl-10 pr-4',
                    ]"
                  >
                    <span :class="[selected ? 'font-medium' : 'font-normal', 'block truncate']">
                      {{ opt }}
                    </span>
                    <span
                      v-if="selected"
                      class="absolute inset-y-0 left-0 flex items-center pl-3 text-aces"
                    >
                      <CheckIcon class="w-5 h-5" />
                    </span>
                  </li>
                </ListboxOption>
              </ListboxOptions>
            </transition>
          </div>
        </Listbox>
      </div>

      <div>
        <label class="form-label">Número de Documento</label>
        <input
          v-model="form.documentNumber"
          type="number"
          required
          class="form-input lg:text-base"
        />
      </div>

      <div>
        <label class="form-label">Email</label>
        <input
          v-model="form.email"
          type="email"
          required
          class="form-input lg:text-base"
        />
        <p
          v-if="emailChanged"
          class="text-xs text-warning-fg mt-1"
        >
          Al cambiar tu email tendrás que verificarlo de nuevo.
        </p>
      </div>

      <div>
        <label class="form-label">Celular</label>
        <input
          v-model="form.phoneNumber"
          type="tel"
          inputmode="numeric"
          maxlength="10"
          required
          :class="[
            'form-input lg:text-base',
            phoneError ? 'border-red-warning' : '',
          ]"
          @input="onPhoneInput"
        />
        <p v-if="phoneError" class="text-xs text-red-warning mt-1">
          {{ phoneError }}
        </p>
        <p
          v-if="phoneChanged"
          class="text-xs text-warning-fg mt-1"
        >
          Al cambiar tu teléfono tendrás que verificarlo de nuevo.
        </p>
      </div>

      <div>
        <label class="form-label">Provincia</label>
        <Listbox v-model="selectedProvince">
          <div class="relative">
            <ListboxButton
              class="form-listbox-btn lg:text-base"
            >
              <span class="block truncate">
                {{ selectedProvince?.provinceName || provincePlaceholder }}
              </span>
              <span
                class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none"
              >
                <SelectorIcon class="w-5 h-5 text-gray-400" aria-hidden="true" />
              </span>
            </ListboxButton>
            <transition
              leave-active-class="transition duration-100 ease-in"
              leave-from-class="opacity-100"
              leave-to-class="opacity-0"
            >
              <ListboxOptions
                class="absolute w-full py-1 mt-1 overflow-auto bg-white z-10 rounded-lg shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none"
              >
                <ListboxOption
                  v-slot="{ active, selected }"
                  v-for="province in provinces"
                  :key="province.provinceId"
                  :value="province"
                  as="template"
                >
                  <li
                    :class="[
                      active ? 'text-white bg-aces' : 'text-black',
                      'cursor-default select-none relative py-2 pl-10 pr-4',
                    ]"
                  >
                    <span :class="[selected ? 'font-medium' : 'font-normal', 'block truncate']">
                      {{ province.provinceName }}
                    </span>
                    <span
                      v-if="selected"
                      class="absolute inset-y-0 left-0 flex items-center pl-3 text-aces"
                    >
                      <CheckIcon class="w-5 h-5" />
                    </span>
                  </li>
                </ListboxOption>
              </ListboxOptions>
            </transition>
          </div>
        </Listbox>
      </div>

      <div>
        <label class="form-label">Iglesia</label>
        <Listbox v-model="selectedChurch">
          <div class="relative">
            <input
              type="text"
              autocomplete="off"
              :value="churchSearch"
              :placeholder="selectedChurch ? selectedChurch.churchName : 'Buscar iglesia...'"
              class="form-input lg:text-base"
              @input="onChurchSearch($event.target.value)"
              @focus="searching = true"
            />
            <ListboxOptions
              v-show="searching && filteredChurches.length > 0"
              static
              class="absolute w-full py-1 mt-1 overflow-auto text-sm bg-white z-10 rounded-lg shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none"
            >
              <ListboxOption
                v-slot="{ active, selected }"
                v-for="church in filteredChurches"
                :key="church.churchClientId"
                :value="church"
                as="template"
                @click="onChurchPick"
              >
                <li
                  :class="[
                    active ? 'text-white bg-aces' : 'text-black',
                    'cursor-default select-none relative py-2 pl-10 pr-4',
                  ]"
                >
                  <span :class="[selected ? 'font-medium' : 'font-normal', 'block truncate']">
                    {{ church.churchName }}
                  </span>
                  <span
                    v-if="selected"
                    class="absolute inset-y-0 left-0 flex items-center pl-3"
                    :class="active ? 'text-white' : 'text-aces'"
                  >
                    <CheckIcon class="w-5 h-5" />
                  </span>
                </li>
              </ListboxOption>
            </ListboxOptions>
          </div>
        </Listbox>
      </div>

      <div class="lg:col-span-2">
        <label class="form-label">Coordinador</label>
        <input
          :value="selectedChurch?.coordinatorName || '—'"
          type="text"
          readonly
          class="form-input-readonly"
        />
      </div>

      <div class="lg:col-span-2 mt-2 flex flex-col gap-3 sm:flex-row sm:items-stretch sm:gap-4">
        <button
          type="submit"
          :disabled="saving"
          class="btn-primary-inline lg:text-lg disabled:opacity-60"
        >
          {{ saving ? "Guardando..." : "Guardar Cambios" }}
        </button>
        <button
          type="button"
          :disabled="saving"
          class="btn-secondary-inline lg:text-lg disabled:opacity-60"
          @click="$emit('cancel')"
        >
          Cancelar
        </button>
      </div>
    </form>
  </div>
</template>

<script>
  import axios from "axios";
  import { ref, computed, watch } from "vue";
  import {
    Listbox,
    ListboxButton,
    ListboxOptions,
    ListboxOption,
  } from "@headlessui/vue";
  import { CheckIcon, SelectorIcon } from "@heroicons/vue/solid";
  import { createToast } from "mosha-vue-toastify";
  import {
    INVALID_FORMAT_MESSAGE,
    isValidArgentinePhone,
    normalizePhoneInput,
  } from "../../utils/argentinePhone";

  export default {
    name: "ProfileEditForm",
    components: {
      Listbox,
      ListboxButton,
      ListboxOptions,
      ListboxOption,
      CheckIcon,
      SelectorIcon,
    },
    props: {
      user: { type: Object, required: true },
      initialProvinces: { type: Array, default: () => [] },
      initialChurch: { type: Object, default: null },
    },
    emits: ["cancel", "saved"],
    setup(props) {
      const docList = ["DNI", "LC", "LE"];

      const form = ref({
        firstName: props.user.firstName || "",
        lastName: props.user.lastName || "",
        docType: props.user.docType || "DNI",
        documentNumber: String(props.user.documentNumber || ""),
        email: props.user.email || "",
        phoneNumber: String(props.user.phoneNumber || ""),
      });

      const provinces = ref([]);
      const selectedProvince = ref(null);
      const churches = ref([]);
      const selectedChurch = ref(null);
      const churchSearch = ref("");
      const searching = ref(false);
      const provinceLoadState = ref("idle");
      const saving = ref(false);
      const phoneError = ref("");

      const provincePlaceholder = computed(() => {
        if (provinceLoadState.value === "loading") return "Cargando provincias...";
        return "Sin provincia";
      });

      const filteredChurches = computed(() => {
        if (!churchSearch.value) return churches.value;
        return churches.value.filter((c) =>
          c.churchName.toLowerCase().includes(churchSearch.value.toLowerCase())
        );
      });

      const norm = (v) => (v == null ? "" : String(v).trim());
      const emailChanged = computed(
        () => norm(form.value.email).toLowerCase() !== norm(props.user.email).toLowerCase()
      );
      const phoneChanged = computed(
        () => norm(form.value.phoneNumber) !== norm(props.user.phoneNumber)
      );

      const loadChurchesForProvince = async (provinceId) => {
        try {
          const { data } = await axios.get(
            `Churches/byProvince?provinceId=${provinceId}`
          );
          churches.value = data || [];
        } catch (err) {
          churches.value = [];
          console.log(err);
        }
      };

      const loadProvinces = async () => {
        if (props.initialProvinces?.length) {
          provinces.value = props.initialProvinces;
          provinceLoadState.value = "ready";
          return;
        }
        try {
          provinceLoadState.value = "loading";
          const { data } = await axios.get("Provinces/all");
          provinces.value = data || [];
          provinceLoadState.value = "ready";
        } catch (err) {
          provinces.value = [];
          provinceLoadState.value = "error";
          console.log(err);
        }
      };

      const loadInitialChurch = async () => {
        const churchClientId = props.user.churchClientId;
        if (!churchClientId) return;
        try {
          let data = props.initialChurch;
          if (
            !data ||
            Number(data.churchClientId) !== Number(churchClientId)
          ) {
            const response = await axios.get(`Churches/ById/${churchClientId}`);
            data = response.data;
          }
          if (!data) return;
          if (provinces.value.length) {
            selectedProvince.value =
              provinces.value.find((p) => p.provinceId === data.provinceId) || null;
          }
          if (selectedProvince.value) {
            await loadChurchesForProvince(selectedProvince.value.provinceId);
            selectedChurch.value =
              churches.value.find(
                (c) => Number(c.churchClientId) === Number(churchClientId)
              ) || null;
            if (selectedChurch.value) {
              churchSearch.value = selectedChurch.value.churchName;
            }
          }
        } catch (err) {
          console.log(err);
        }
      };

      watch(
        () => selectedProvince.value?.provinceId,
        async (provinceId, oldId) => {
          if (!provinceId) {
            churches.value = [];
            return;
          }
          if (!oldId) return;
          if (oldId !== provinceId) {
            selectedChurch.value = null;
            churchSearch.value = "";
          }
          await loadChurchesForProvince(provinceId);
          if (selectedChurch.value) {
            selectedChurch.value =
              churches.value.find(
                (c) => c.churchClientId === selectedChurch.value.churchClientId
              ) || null;
          }
        }
      );

      (async () => {
        await loadProvinces();
        await loadInitialChurch();
      })();

      const onChurchSearch = (val) => {
        churchSearch.value = val;
        searching.value = true;
      };
      const onChurchPick = () => {
        searching.value = false;
        if (selectedChurch.value) churchSearch.value = selectedChurch.value.churchName;
      };

      const onPhoneInput = () => {
        form.value.phoneNumber = normalizePhoneInput(form.value.phoneNumber);
        if (!form.value.phoneNumber) {
          phoneError.value = "";
          return;
        }
        phoneError.value = isValidArgentinePhone(form.value.phoneNumber)
          ? ""
          : INVALID_FORMAT_MESSAGE;
      };

      return {
        docList,
        form,
        provinces,
        selectedProvince,
        churches,
        selectedChurch,
        churchSearch,
        searching,
        filteredChurches,
        provincePlaceholder,
        emailChanged,
        phoneChanged,
        saving,
        phoneError,
        onChurchSearch,
        onChurchPick,
        onPhoneInput,
      };
    },
    methods: {
      async onSave() {
        const auth = this.user;
        const clientId = auth.clientId ?? auth.ClientId;
        if (!clientId) {
          createToast("No se pudo identificar el usuario.", {
            position: "top-center",
            type: "danger",
            transition: "zoom",
          });
          return;
        }

        this.onPhoneInput();
        if (!isValidArgentinePhone(this.form.phoneNumber)) {
          this.phoneError = INVALID_FORMAT_MESSAGE;
          createToast(INVALID_FORMAT_MESSAGE, {
            position: "top-center",
            type: "danger",
            transition: "zoom",
          });
          return;
        }
        this.form.phoneNumber = normalizePhoneInput(this.form.phoneNumber);

        const effectiveChurchId =
          this.selectedChurch?.churchClientId ?? auth.churchClientId;
        const churchChanged =
          Number(effectiveChurchId || 0) !== Number(auth.churchClientId || 0);

        this.saving = true;
        try {
          if (churchChanged) {
            try {
              const { data: period } = await axios.get(`Periods/getCurrent`);
              await axios.patch(
                `Clients/changeSubscriptorChurch?clientId=${clientId}&churchClientId=${effectiveChurchId}&periodId=${period.periodId}`
              );
            } catch (err) {
              console.log(err.response);
              createToast(
                err.response?.data ||
                  "No se pudo cambiar la iglesia. Intentá nuevamente.",
                {
                  position: "top-center",
                  type: "danger",
                  transition: "zoom",
                }
              );
              return;
            }
          }

          const payload = {
            ...auth,
            clientId,
            firstName: this.form.firstName,
            lastName: this.form.lastName,
            docType: this.form.docType,
            documentNumber: this.form.documentNumber,
            email: this.form.email,
            phoneNumber: this.form.phoneNumber,
            churchClientId: effectiveChurchId,
          };

          const { data } = await axios.put(`Clients/update`, payload);

          createToast("Datos actualizados correctamente.", {
            position: "top-center",
            type: "success",
            transition: "zoom",
          });

          this.$emit("saved", {
            updatedUser: data,
            emailChanged: this.emailChanged,
            phoneChanged: this.phoneChanged,
          });
        } catch (err) {
          if (err.response?.status === 406) {
            createToast(
              `Ya existe un usuario con el documento ${this.form.docType} ${this.form.documentNumber}`,
              {
                position: "top-center",
                type: "danger",
                transition: "zoom",
              }
            );
          } else {
            createToast(
              err.response?.data?.message ||
                err.response?.data ||
                "No se pudieron guardar los cambios.",
              {
                position: "top-center",
                type: "danger",
                transition: "zoom",
              }
            );
          }
          console.log(err.response);
        } finally {
          this.saving = false;
        }
      },
    },
  };
</script>
